// src/HeadScene.jsx
// What waits at the far end of the binary tunnel: a white head on white, ringed
// by arrows. Clicking an arrow turns the head a quarter turn away from the
// direction that arrow points. Clicking the head itself navigates onward,
// based on which way it's currently facing.
//
// The head never turns past a quarter turn in any direction, so you never end
// up looking at the back of it. An arrow retires as soon as it has been used
// and only its opposite remains, which is the way back to front-on. The down
// arrow exists solely to undo the up arrow, so it isn't there to begin with.
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";

import { Head } from "./blogs/HeadModel.jsx";
import { BLOG_ROUTES } from "./blogs/postLinks.js";
import { HEAD_SIZE, useFittedModel } from "./blogs/headModelUtils.js";

const BASE = import.meta.env.BASE_URL;

const ARROW_URL = `${BASE}blog/arrow.glb`;

// Each model is auto-centred and scaled so its largest dimension lands here,
// which keeps the layout stable if either .glb is swapped out.
const ARROW_SIZE = 0.4;

/* Arrow placement. arrow.glb is authored as a spike pointing down (-Y), so
 * `roll` is the turn about Z that aims it. `yaw`/`pitch` are the steps that
 * arrow adds to the head's position, in quarter turns. */
const ARROW_X = 1.55;
const ARROW_Y = 1.28;

// On narrow viewports the head grows (below) and needs more headroom, so the
// up/down arrows sit further out than their baseline placement there.
const ARROW_Y_MOBILE = 1.62;

// Shifts the whole head down a touch so it clears the up arrow with more
// breathing room, on every viewport.
const HEAD_Y_OFFSET = -0.16;

// The head grows relative to the rest of the composition on narrow
// viewports, independent of the uniform `fit` scale applied to everything.
const HEAD_MOBILE_SCALE = 1.3;

// Matches the breakpoint used elsewhere in the app (see App.jsx `isMobile`).
const MOBILE_BREAKPOINT = 768;

const ARROWS = [
  { id: "left", roll: -Math.PI / 2, position: [-ARROW_X, -0.05, 0], yaw: 1 },
  { id: "right", roll: Math.PI / 2, position: [ARROW_X, -0.05, 0], yaw: -1 },
  { id: "up", roll: Math.PI, position: [0, ARROW_Y, 0], pitch: 1 },
  { id: "down", roll: 0, position: [0, -ARROW_Y, 0], pitch: -1 },
];

const ARROW_IDLE = "#d5e8f2";
const ARROW_HOVER = "#ffffff";
const ARROW_HOVER_GROW = 1.16;

// The composition is laid out for this width in world units; narrower
// viewports scale the whole group down so the arrows stay on screen.
const DESIGN_WIDTH = 3.8;

/**
 * Which blog scene the head sends you to, based on its current rotation:
 * facing the crown up (pitch=1) reads as looking "up and out" -> thoughts (raw);
 * turned to either side (yaw!=0) -> longer (refined); front-on, the default -> content (polished).
 */
function resolveHeadDestination(yaw, pitch) {
  if (pitch === 1) return BLOG_ROUTES.thoughts;
  if (yaw !== 0) return BLOG_ROUTES.longer;
  return BLOG_ROUTES.content;
}

/* -----------------------------------------------------------------------------
 * Arrows
 * -------------------------------------------------------------------------- */

function Arrow({ roll, position, shown, onSelect }) {
  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: ARROW_IDLE,
        roughness: 0.5,
        metalness: 0.05,
      }),
    []
  );
  useEffect(() => () => material.dispose(), [material]);

  const { object, scale, offset } = useFittedModel(ARROW_URL, ARROW_SIZE, material);

  const groupRef = useRef(null);
  const hoveredRef = useRef(false);
  const startedRef = useRef(false);
  const swatch = useMemo(() => new THREE.Color(), []);

  // Drop hover the moment an arrow retires, or the cursor keeps its hand shape
  // over what is now empty space.
  useEffect(() => {
    if (!shown && hoveredRef.current) {
      hoveredRef.current = false;
      document.body.style.cursor = "";
    }
  }, [shown]);

  useEffect(
    () => () => {
      document.body.style.cursor = "";
    },
    []
  );

  useFrame((_, dt) => {
    const group = groupRef.current;
    if (!group) return;

    // Snap to the right size on the first frame so an arrow that starts
    // retired doesn't flash in before shrinking away.
    if (!startedRef.current) {
      startedRef.current = true;
      group.scale.setScalar(shown ? 1 : 0);
    }

    const step = Math.min(dt, 0.1);
    const target = shown ? (hoveredRef.current ? ARROW_HOVER_GROW : 1) : 0;
    const next = THREE.MathUtils.damp(group.scale.x, target, 9, step);

    group.scale.setScalar(next);
    group.visible = next > 0.01; // invisible also means un-clickable

    material.color.lerp(
      swatch.set(hoveredRef.current ? ARROW_HOVER : ARROW_IDLE),
      1 - Math.exp(-9 * step)
    );
  });

  // Hover lives in a ref: the frame loop reads it, so there's nothing to
  // re-render on pointer move.
  const setHovered = (value) => {
    hoveredRef.current = value;
    document.body.style.cursor = value ? "pointer" : "";
  };

  return (
    <group
      ref={groupRef}
      position={position}
      onClick={(event) => {
        event.stopPropagation();
        if (shown) onSelect();
      }}
      onPointerOver={(event) => {
        event.stopPropagation();
        if (shown) setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
    >
      <group rotation={[0, 0, roll]}>
        <group position={offset} scale={scale}>
          <primitive object={object} />
        </group>
      </group>
    </group>
  );
}

/* -----------------------------------------------------------------------------
 * Stage
 * -------------------------------------------------------------------------- */

function Stage({ onNavigate }) {
  // Quarter turns away from front-on. Yaw is held to one step either side so
  // the back of the head is never reachable; pitch only goes one way, with the
  // down arrow appearing to bring it back.
  const [yaw, setYaw] = useState(0);
  const [pitch, setPitch] = useState(0);

  const { viewport, size } = useThree();
  const isMobile = size.width <= MOBILE_BREAKPOINT;
  const fit = THREE.MathUtils.clamp(viewport.width / DESIGN_WIDTH, 0.34, 1);
  const arrowY = isMobile ? ARROW_Y_MOBILE : ARROW_Y;

  const rotate = useCallback((arrow) => {
    if (arrow.yaw) setYaw((v) => THREE.MathUtils.clamp(v + arrow.yaw, -1, 1));
    if (arrow.pitch) setPitch((v) => THREE.MathUtils.clamp(v + arrow.pitch, 0, 1));
  }, []);

  const handleHeadSelect = useCallback(() => {
    onNavigate?.(resolveHeadDestination(yaw, pitch));
  }, [onNavigate, yaw, pitch]);

  // An arrow is offered only while it has somewhere to go: yaw may sit one step
  // either side of front, pitch one step up. Everything else is off-limits, so
  // the arrow that would take you there simply isn't there.
  const isShown = (arrow) => {
    if (arrow.yaw) {
      const next = yaw + arrow.yaw;
      return next >= -1 && next <= 1;
    }

    const next = pitch + arrow.pitch;
    return next >= 0 && next <= 1;
  };

  // Arrow keys mirror the on-screen arrows, so the control isn't mouse-only.
  useEffect(() => {
    const byKey = {
      ArrowLeft: "left",
      ArrowRight: "right",
      ArrowUp: "up",
      ArrowDown: "down",
    };

    const onKeyDown = (event) => {
      const id = byKey[event.key];
      if (!id) return;
      const arrow = ARROWS.find((a) => a.id === id);
      if (arrow) rotate(arrow);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [rotate]);

  return (
    <group scale={fit}>
      <group position={[0, HEAD_Y_OFFSET, 0]}>
        <Head
          yaw={yaw}
          pitch={pitch}
          size={isMobile ? HEAD_SIZE * HEAD_MOBILE_SCALE : undefined}
          onSelect={onNavigate ? handleHeadSelect : undefined}
        />
      </group>

      {ARROWS.map((arrow) => (
        <Arrow
          key={arrow.id}
          roll={arrow.roll}
          position={
            arrow.id === "up"
              ? [0, arrowY, 0]
              : arrow.id === "down"
              ? [0, -arrowY, 0]
              : arrow.position
          }
          shown={isShown(arrow)}
          onSelect={() => rotate(arrow)}
        />
      ))}
    </group>
  );
}

/** A missing or broken .glb shouldn't take the page down with it. */
class HeadBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn("[HeadScene] scene failed to load:", error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/* -----------------------------------------------------------------------------
 * Export
 * -------------------------------------------------------------------------- */

useGLTF.preload(ARROW_URL);

export default function HeadScene({ onNavigate }) {
  return (
    // Transparent canvas over the page's white, matching how the main scene is
    // composited. Lighting mirrors MainMenu's so the two read as one world.
    <Canvas
      dpr={[1, 1.75]}
      gl={{ antialias: true, alpha: true }}
      camera={{ fov: 40, near: 0.1, far: 60, position: [0, 0, 4.4] }}
    >
      <ambientLight intensity={0.5} color="#ffffff" />
      <directionalLight position={[5, 10, 2]} intensity={5} color="#f2feff" />
      {/* soft fill opposite the key, so the shaded side stays white rather
          than dropping to grey against the background */}
      <directionalLight position={[-4, -1, 3]} intensity={1.1} color="#eef4ff" />

      <HeadBoundary>
        <Suspense fallback={null}>
          <Stage onNavigate={onNavigate} />
        </Suspense>
      </HeadBoundary>
    </Canvas>
  );
}
